import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

const CompanyPage = () => {
  useEffect(() => {
    // SEO設定
    document.title = '会社概要 | 株式会社伊藤総業 - 東京都世田谷区の総合企業';
    
    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "会社概要 - 株式会社伊藤総業",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`,
      "description": "株式会社伊藤総業の会社概要。東京都世田谷区を拠点とする総合企業として、中古車販売から再生可能エネルギー、不動産まで幅広い事業を展開。",
      "mainEntity": {
        "@type": "Organization",
        "name": "株式会社伊藤総業",
        "foundingDate": "2020-04",
        "founder": {
          "@type": "Person",
          "name": "伊藤銀星"
        },
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "代沢五丁目二十四番一号",
          "addressLocality": "世田谷区",
          "addressRegion": "東京都",
          "postalCode": "155-0032",
          "addressCountry": "JP"
        },
        "telephone": "03-6764-9633",
        "faxNumber": "03-6764-9633",
        "numberOfEmployees": "15"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <main>
        <section 
          className="relative h-96 bg-cover bg-center flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/b3c2a66b593a9afb4ca3582d4523d8b2.jpg')`
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-white max-w-2xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">会社概要</h1>
              <p className="text-xl mb-6">
                多様な事業領域で社会に貢献する総合企業として、持続可能な未来を創造します
              </p>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">株式会社伊藤総業について</h2>
                <p className="text-lg text-gray-600 mb-6">
                  株式会社伊藤総業は、東京都世田谷区を拠点とする総合企業として、中古車販売から再生可能エネルギー、不動産まで幅広い事業を展開しています。
                </p>
                <p className="text-gray-600 mb-6">
                  私たちは常に時代の変化を先読みし、社会のニーズに応える新しい価値を創造し続けています。持続可能な社会の実現に向けて、環境に配慮した事業展開を心がけ、お客様と地域社会に貢献することを使命としています。
                </p>
                <p className="text-gray-600">
                  多様な事業領域での豊富な経験と専門知識を活かし、お客様一人ひとりのニーズに最適なソリューションを提供いたします。
                </p>
              </div>

              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20business%20team%20in%20modern%20office%20environment%2C%20diverse%20group%20of%20employees%20working%20together%2C%20clean%20corporate%20interior%20with%20natural%20lighting%2C%20representing%20successful%20company%20culture%20and%20teamwork&width=600&height=400&seq=company-team&orientation=landscape"
                  alt="株式会社伊藤総業チーム"
                  className="rounded-lg shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">会社情報</h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">会社名</h3>
                      <p className="text-gray-600">株式会社伊藤総業</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">代表者</h3>
                      <p className="text-gray-600">代表取締役 伊藤銀星</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">所在地</h3>
                      <p className="text-gray-600">
                        〒155-0032<br />
                        東京都世田谷区代沢五丁目二十四番一号
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">電話番号</h3>
                      <p className="text-gray-600">03-6764-9633</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">FAX番号</h3>
                      <p className="text-gray-600">03-6764-9633</p>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">資本金</h3>
                      <p className="text-gray-600">100万円</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Business Areas */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">事業領域</h2>
              <p className="text-lg text-gray-600">
                多様な分野で専門性を活かしたサービスを提供しています
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-600">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-car-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">中古車事業</h3>
                <p className="text-gray-600 mb-4">
                  中古車の販売・買取・輸出を通じて、お客様のカーライフをサポートします。
                </p>
                <a 
                  href="https://carproud.jp" 
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 font-semibold cursor-pointer"
                >
                  詳細を見る →
                </a>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-600">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-leaf-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">再生可能エネルギー</h3>
                <p className="text-gray-600 mb-4">
                  太陽光発電システムの企画・開発・販売により、持続可能な社会を実現します。
                </p>
                <Link 
                  to="/renewable-energy" 
                  className="text-green-600 hover:text-green-700 font-semibold cursor-pointer"
                >
                  詳細を見る →
                </Link>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-600">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-battery-charge-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">蓄電所運営</h3>
                <p className="text-gray-600 mb-4">
                  蓄電所の企画・開発・設計・運営により、エネルギーの安定供給を実現します。
                </p>
                <Link 
                  to="/energy-storage" 
                  className="text-orange-600 hover:text-orange-700 font-semibold cursor-pointer"
                >
                  詳細を見る →
                </Link>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-600">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-computer-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">ホームページ制作</h3>
                <p className="text-gray-600 mb-4">
                  企画から制作、運営・管理まで、Webサイトの総合的なサポートを提供します。
                </p>
                <Link 
                  to="/web-development" 
                  className="text-purple-600 hover:text-purple-700 font-semibold cursor-pointer"
                >
                  詳細を見る →
                </Link>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-red-600">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">不動産売買</h3>
                <p className="text-gray-600 mb-4">
                  住宅用・商業用・投資用不動産の売買を通じて、お客様の資産形成をサポートします。
                </p>
                <Link 
                  to="/real-estate" 
                  className="text-red-600 hover:text-red-700 font-semibold cursor-pointer"
                >
                  詳細を見る →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-target-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">企業理念</h3>
                <p className="text-gray-600 mb-4">
                  私たちは、多様な事業領域での専門性を活かし、お客様と社会に価値を提供し続けます。
                </p>
                <p className="text-gray-600">
                  持続可能な社会の実現に向けて、環境に配慮した事業展開を通じて、次世代により良い未来を残すことを使命としています。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">ビジョン</h3>
                <p className="text-gray-600 mb-4">
                  革新的なソリューションと誠実なサービスにより、お客様の信頼を獲得し、業界のリーディングカンパニーとなることを目指します。
                </p>
                <p className="text-gray-600">
                  地域社会との共生を大切にし、社会全体の発展に貢献する企業として成長し続けます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Map */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">アクセス</h2>
              <p className="text-lg text-gray-600">
                東京都世田谷区代沢五丁目二十四番一号
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold mb-4">交通アクセス</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <i className="ri-train-line text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold">小田急小田原線</p>
                      <p className="text-gray-600 text-sm">下北沢駅より徒歩8分</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <i className="ri-subway-line text-green-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold">京王井の頭線</p>
                      <p className="text-gray-600 text-sm">下北沢駅より徒歩8分</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <i className="ri-car-line text-orange-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="font-semibold">お車でお越しの場合</p>
                      <p className="text-gray-600 text-sm">首都高速3号渋谷線 池尻ICより約10分</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold mb-2">お問い合わせ</h4>
                  <p className="text-gray-600 mb-2">
                    <i className="ri-phone-line mr-2"></i>
                    03-6764-9633
                  </p>
                  <p className="text-gray-600 mb-2">
                    <i className="ri-printer-line mr-2"></i>
                    FAX: 03-6764-9633
                  </p>
                  <p className="text-gray-600 text-sm">
                    営業時間：平日 9:00-18:00
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">お気軽にお問い合わせください</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              各事業に関するご相談やお見積もりなど、お気軽にお問い合わせください。専門スタッフが丁寧に対応いたします。
            </p>
            <Link 
              to="/contact" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              お問い合わせ
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CompanyPage;
